import React from "react";
import {
    DataGrid,
} from "@mui/x-data-grid";
import {
    Box,
    Button,
    Chip,
    Typography,
    Modal,
    Tabs,
    Tab
} from "@mui/material";
import {
    CheckCircle,
    Edit
} from 'react-feather';
import { useDispatch, useSelector } from "react-redux";
import EstimateViewer from "./SentEstimateViewer";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import SplitToken from '../../mixins/SplitToken';
import Page from '../../components/Page';
import { formatMoney } from '../../utils/moneyFormat';
import PaymentSummary from "./PaymentSummary";
import { invoiceTotalValueGetter } from '../../utils/columnValueGetters';
import { useCompanyInvoices, useDeleteEstimate } from '../../hooks/useInvoices';
import { EMPTY_GUID } from '../../query/queryConfig';

function EstimatesFromMeTable(){
    /// view all invoices sent by company to customers ///
    const user = SplitToken();
    const company = useSelector((state) => state.CompanyReducer.userCompanies[0]);
    const deleteEstimate = useDeleteEstimate();
    const customerSelect = useSelector((state) => state.UserReducer.customerSelection);

    const startDateInitialState = dayjs().subtract(1, "years").startOf('day');
    const endDateInitialState = dayjs().endOf('day');
    const [startDate, setStartDate] = React.useState(null);
    const [endDate, setEndDate] = React.useState(null);
    const [exportStartDate, setExportStartDate] = React.useState(dayjs(startDateInitialState).format());
    const [exportEndDate, setExportEndDate] = React.useState(dayjs(endDateInitialState).format());

    function resetDate(){
        setStartDate(null);
        setEndDate(null);
        setExportStartDate(startDateInitialState);
        setExportEndDate(endDateInitialState);
    };

    const [paginationModel, setPaginationModel] = React.useState({pageSize: 25, page: 0});

    const { data: invoiceData, isFetching: isLoading } = useCompanyInvoices({
        companyId: company?.id,
        customerId: customerSelect?.userCompanyId,
        startDate: exportStartDate,
        endDate: exportEndDate,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        enabled: Boolean(company?.id && customerSelect?.userCompanyId != null),
    });

    const CompanyInvoices = invoiceData?.invoicesDTO ?? [];
    const foundInvoiceCount = invoiceData?.foundTotalInvoiceCount ?? 0;
    const invoiceCount = invoiceData?.totalInvoiceCount ?? 0;

    const rowCountRef = React.useRef(foundInvoiceCount || 0);

    const rowCount = React.useMemo(() => {
      if (foundInvoiceCount !== undefined) {
        rowCountRef.current = foundInvoiceCount;
      }
      return rowCountRef.current;
    }, [foundInvoiceCount]);

    function applyInvoiceDateFilter() {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    const [invoiceModelObj, setInvoiceModelObj] = React.useState();
    const [invoiceModal, setInvoiceModal] = React.useState(false);
    const handleInvoiceOpen = () => setInvoiceModal(true);
    const handleInvoiceClose = () => setInvoiceModal(false);
    function showInvoice(x){
        setInvoiceModelObj({
            created: x?.created,
            customerEmail: x?.customerEmail,
            customerId: x?.customerId,
            customerName: x?.customerName,
            developerId: x?.developerId,
            developerName: x?.developerName,
            estimateId: x?.estimateId,
            fileCompanyId: x?.fileCompanyId,
            fileId: x?.fileId,
            fileIsAccepted: x?.fileIsAccepted,
            fileName: x?.fileName,
            id: x?.id,
            invoiceValues: x?.invoiceValues,
            isInvoiceAccepted: x?.isInvoiceAccepted,
            isPaidFor: x?.isPaidFor,
            isProcessed: x?.isProcessed,
            isSent: x?.isSent,
            jobId: x?.jobId,
            openInvoice: x?.openInvoice,
            pointCount: x?.pointCount,
            reportUrl: x?.reportUrl,
            sequencingErrorCode: x?.sequencingErrorCode,
            summaryUrl: x?.summaryUrl,
            updated: x?.updated,
            pointList: x?.pointList,
        })
        handleInvoiceOpen();
    };

    /// For Creating and Downloading Invoice as CSV //
    function exportToCsv() {
        let csvDownloadable = [];
        let exportECU = 0;
        let exportFinalTotal = 0;
        let exportStartDateFormat = dayjs(exportStartDate).format('MM/DD/YYYY');
        let exportEndDateFormat = dayjs(exportEndDate).format('MM/DD/YYYY');

        csvDownloadable.push(
            [`Date`,`Customer`,`Plan`,`Point Total`,`Sale Total`]
        );

        for (let i = 0; i < CompanyInvoices.length; i++){
            let data = [];
            let invoiceTotal = 0;
            let taxTotal = 0;
            let finalTotal = 0;
            if (CompanyInvoices[i]?.invoiceValues != undefined) {
                data = JSON?.parse(CompanyInvoices[i]?.invoiceValues)
                if(data.items != null){
                    for (let i = 0; i < data.items.length; i++) {
                        invoiceTotal += data.items[i].total;
                    }
                }

                if(data.taxes != null){
                    for (let i = 0; i < data.taxes.length; i++) {
                        taxTotal += Number(data.taxes[i].total);
                    }
                }
                finalTotal = taxTotal+invoiceTotal

                exportFinalTotal += finalTotal;

                if(data.pointTypeTotal){
                    exportECU += data.pointTypeTotal;
                }
            };

            if(CompanyInvoices[i].invoiceJobName != null){
                csvDownloadable.push(
                    [`${data?.date}`,`${CompanyInvoices[i].customerName}`,`${CompanyInvoices[i].invoiceJobName}`,`${data?.pointTypeTotal}`,`${formatMoney(finalTotal)}`]
                );
            } else {
                csvDownloadable.push(
                    [`${data?.date}`,`${CompanyInvoices[i].customerName}`,`${CompanyInvoices[i].fileName}`,`${data?.pointTypeTotal}`,`${formatMoney(finalTotal)}`]
                );
            }
        };

        csvDownloadable.push(
            [`${exportStartDateFormat} - ${exportEndDateFormat}`,``,``,`${exportECU}`,`${formatMoney(exportFinalTotal)}`]
        );

        let csvContent = csvDownloadable.map(e => e.join(",")).join("\n");

        var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        if (navigator.msSaveBlob) { // IE 10+
            if(customerSelect.userCompanyId != EMPTY_GUID){
                navigator.msSaveBlob(blob, `${customerSelect.userCompanyId}-${exportStartDateFormat}-${exportEndDateFormat}`);
            } else {
                navigator.msSaveBlob(blob, `allCustomerEstimates-${exportStartDateFormat}-${exportEndDateFormat}`);
            }
        } else {
            var link = document.createElement("a");
            if (link.download !== undefined) { // feature detection
                // Browsers that support HTML5 download attribute
                var url = URL.createObjectURL(blob);
                link.setAttribute("href", url);
                if(customerSelect.userCompanyId != EMPTY_GUID){
                    link.setAttribute("download", `${customerSelect.userCompanyId}-${exportStartDateFormat}-${exportEndDateFormat}`);
                } else {
                    link.setAttribute("download", `allCustomerEstimates-${exportStartDateFormat}-${exportEndDateFormat}`);
                }
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    };

    let dataCallSettings = {
        companyId: company?.id,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        customerId: customerSelect.userCompanyId,
        startDate: exportStartDate,
        endDate: exportEndDate
    };

    const columns = [
        { field: 'customerCompany', headerName: 'Customer', width: 200, headerClassName: 'header', editable: false },
        { field: 'invoiceJobName', headerName: 'Project Name', width: 250, headerClassName: 'header', editable: false},
        { field: 'invoice', headerName: 'Estimate', width: 200, headerClassName: 'header', sortable: false, renderCell: (params) => (
            Invoice(params.row)
        )},
        { field: 'total', headerName: 'Total', width: 100, headerClassName: 'header', valueGetter: invoiceTotalValueGetter,
            renderCell: (params) => {
            var data = JSON?.parse(params.row?.invoiceValues);
            if(data?.finalTotal){
                return (<div>$ {data?.finalTotal}</div>)
            } else {
                return (<div>N/A</div>)
            }
        }},
        { field: 'updated', headerName: 'Updated', width: 200, headerClassName: 'header', renderCell: (params) => {
            let date = new Date(params.row.updated);
            return date.toLocaleString()
        }},
        { field: 'Delete', headerName: 'Delete', width: 125, headerClassName: 'header', sortable: false,
            renderCell: (cellValues) => {
                return(
                    <Button className="delBtn" variant="contained" sx={{ bgcolor: `error.light`}}
                        onClick={() => {
                            swal({
                                title: "DELETE ESTIMATE",
                                text: `Delete this estimate forever? This cannot be undone.`,
                                icon: "warning",
                                buttons: true,
                                dangerMode: false,
                            })
                            .then((willUpdate) => {
                                if (willUpdate) {
                                    deleteEstimate.mutate({ estimateId: cellValues.row.id })
                                } else {
                                    swal({
                                        title: `Estimate not Deleted.`
                                    });
                            }})
                    }}>
                        Delete
                    </Button>
                )
            }
        },
    ];

    function Invoice(x) {
        if(x.jobId == null && x.isSent == false){
            return (
                // download invoice
                <div>
                    <Button variant="contained" color="info" onClick={() =>
                        handleOpenEstimate(x)
                    }>
                        <Edit/>
                        <span style={{marginLeft:'5px'}}>Unsent</span>
                    </Button>
                </div>
            )
        }
        else if(x.jobId == null && x.isSent == true && x.customerInvoiceStatus == 1){
            return (
                // download invoice
                <div>
                    <Button variant="contained" color="secondary" onClick={() =>
                        handleOpenEstimate(x)
                    }>
                        <Edit/>
                        <span style={{marginLeft:'5px'}}>Sent</span>
                    </Button>
                </div>
            )
        }
        else if(x.jobId == null && x.isSent == true && x.customerInvoiceStatus == 2){
            return (
                // download invoice
                <div>
                    <Button variant="contained" color="success" onClick={() =>
                        handleOpenEstimate(x)
                    }>
                        Done
                    </Button>
                </div>
            )
        }
        else if(x.jobId != null){
            return(
                // download invoice
                <div>
                    <Button variant="contained" color="success" onClick={() =>
                        showInvoice(x)
                    }>
                        View
                    </Button>
                </div>
            )
        }
    };

    const rows = CompanyInvoices;

    const [openEstimate, setOpenEstimate] = React.useState(false);
    function handleOpenEstimate(x){
        setOpenEstimate(true)
        if(x != null){
            setInvoiceModelObj({
                created: x?.created,
                customerEmail: x?.customerEmail,
                customerId: x?.customerId,
                customerName: x?.customerName,
                companyContact: x?.customerCompany,
                developerId: x?.developerId,
                developerName: x?.developerName,
                estimateId: x?.estimateId,
                fileCompanyId: x?.fileCompanyId,
                fileId: x?.fileId,
                fileIsAccepted: x?.fileIsAccepted,
                fileName: x?.fileName,
                id: x?.id,
                invoiceValues: x?.invoiceValues,
                isInvoiceAccepted: x?.isInvoiceAccepted,
                isPaidFor: x?.isPaidFor,
                isProcessed: x?.isProcessed,
                isSent: x?.isSent,
                jobId: x?.jobId,
                openInvoice: x?.openInvoice,
                pointCount: x?.pointCount,
                reportUrl: x?.reportUrl,
                sequencingErrorCode: x?.sequencingErrorCode,
                summaryUrl: x?.summaryUrl,
                updated: x?.updated,
                pointList: x?.pointList,
                invoiceJobName: x?.invoiceJobName
            })
        }
    };
    function handleCloseEstimate(){
        setOpenEstimate(false);
        setInvoiceModelObj();
    };

    let dispatchSettings = {
        companyId: company?.id,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        customerId: customerSelect.userCompanyId,
        startDate: exportStartDate,
        endDate: exportEndDate,
    }

    return (
        <Page title="Customer Estimates">
                <Box style={{paddingRight:'30px', height: '65vh'}}>
                    <Box sx={{ marginTop: "10px", marginBottom: "10px" }}>
                        <Typography textAlign={"center"} color={"blue"} variant='h4'>
                            Customer Estimates
                        </Typography>
                        <Typography textAlign={"center"}>
                            Total Estimates: {invoiceCount}
                        </Typography>
                    </Box>
                    <Button variant="contained" color="primary" onClick={() => {
                        handleOpenEstimate();
                    }}>
                        Create New Estimate
                    </Button>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box style={{display:'flex', flexDirection:'row'}}>
                            <Box sx={{ m: 1 }}>
                                <DatePicker
                                    views={['year', 'month', 'day']}
                                    className='DatePicker'
                                    label="Start Date"
                                    value={startDate}
                                    onChange={ (selectedValue) => {
                                        setStartDate(dayjs(selectedValue).startOf('day'));
                                        setExportStartDate(dayjs(selectedValue).startOf('day').format());
                                    }}
                                    renderInput={ (params) => <TextField {...params} />}
                                />
                            </Box>

                            <Box sx={{ m: 1 }} >
                                <DatePicker
                                    className='DatePicker'
                                    label="End Date"
                                    value={endDate}
                                    onChange={ (selectedValue) => {
                                        setEndDate(dayjs(selectedValue).endOf('day'));
                                        setExportEndDate(dayjs(selectedValue).endOf('day').format());
                                    }}
                                    renderInput={ (params) => <TextField {...params} />}
                                />
                            </Box>
                            <Button variant='contained' style={{height:'75%', alignSelf:'center', margin:'auto'}} onClick={applyInvoiceDateFilter}>Filter List</Button>
                            <Button variant='outlined' color='warning' style={{height:'75%', alignSelf:'center', margin:'auto'}} onClick={()=>{resetDate()}}>Reset List</Button>
                            <Button variant='contained' color='secondary' style={{height:'75%', alignSelf:'center', margin:'auto'}} onClick={()=>{exportToCsv()}}>Export Estimate Data</Button>
                        </Box>
                    </LocalizationProvider>
                    <DataGrid
                        columns={columns}
                        rows={rows}
                        loading={isLoading}
                        pageSizeOptions={[10, 25, 50, 100]}
                        rowCount={rowCount}
                        paginationMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        style={{textAlign:'left'}}
                    />
                    <Modal
                        className="invoiceModal"
                        open={invoiceModal}
                        onClose={handleInvoiceClose}
                        hideBackdrop={true}
                    >
                        <div>
                            <Button onClick={() => setInvoiceModal(false)}>Close</Button>
                            <EstimateViewer className="estimateDiv" item={invoiceModelObj} handleInvoiceClose={handleInvoiceClose}/>
                        </div>
                    </Modal>
                    <Modal
                        open={openEstimate}
                        onClose={handleCloseEstimate}
                        aria-labelledby="modal-modal-title"
                        aria-describedby="modal-modal-description"
                        className="estimateModal"
                        hideBackdrop={true}
                    >
                        <div className="estimateDiv">
                            <PaymentSummary className="estimateDiv" item={invoiceModelObj} handleCloseEstimate={handleCloseEstimate} dispatchSettings={dispatchSettings}/>
                        </div>
                    </Modal>
                </Box>
        </Page>
    )
}

export default EstimatesFromMeTable;